import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMoney } from "../lib/format";
import { MapPin, Navigation, Truck, Loader2, CheckCircle2, Search } from "lucide-react";

const EQUIPMENT = ["van", "reefer", "flatbed", "stepdeck", "tanker"];

export default function DriverMatches() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ home_zip: "", service_radius_miles: 150, equipment_type: "" });
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get("/matches/loads");
    setData(r.data);
    setForm((f) => ({
      home_zip: r.data.home_zip || "",
      service_radius_miles: r.data.radius || 150,
      equipment_type: r.data.equipment_type || "",
    }));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.put("/me/driver-profile", {
        home_zip: form.home_zip,
        service_radius_miles: Number(form.service_radius_miles),
        equipment_type: form.equipment_type || null,
      });
      if (refresh) await refresh();
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not save profile");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">For drivers</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">Loads near you</h1>
        <p className="text-slate-500 mt-2">Set your home base and we’ll automatically match published loads within your range — and alert you when new ones drop.</p>
      </div>

      {/* Profile */}
      <form onSubmit={save} className="card-base p-6 grid md:grid-cols-4 gap-4 items-end" data-testid="driver-profile-form">
        <div>
          <label className="label-base">Home ZIP</label>
          <input className="input-base" placeholder="e.g. 60616" value={form.home_zip}
            onChange={(e) => setForm({ ...form, home_zip: e.target.value })} required data-testid="profile-zip" />
        </div>
        <div>
          <label className="label-base">Search radius (mi)</label>
          <input type="number" min="10" max="1000" className="input-base" value={form.service_radius_miles}
            onChange={(e) => setForm({ ...form, service_radius_miles: e.target.value })} data-testid="profile-radius" />
        </div>
        <div>
          <label className="label-base">My equipment</label>
          <select className="input-base capitalize" value={form.equipment_type}
            onChange={(e) => setForm({ ...form, equipment_type: e.target.value })} data-testid="profile-equipment">
            <option value="">Any</option>
            {EQUIPMENT.map((eq) => <option key={eq} value={eq} className="capitalize">{eq}</option>)}
          </select>
        </div>
        <button className="btn-primary" disabled={saving} data-testid="profile-save">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          {saving ? "Saving…" : "Save & match"}
        </button>
        {err && <div className="md:col-span-4 text-sm text-red-600 font-semibold" data-testid="profile-error">{err}</div>}
      </form>

      {/* Matches */}
      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Finding loads…</div>
      ) : !data?.home_zip ? (
        <div className="card-base p-10 text-center" data-testid="no-zip">
          <MapPin size={28} className="text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-bold text-navy">Set your home ZIP to see matches</div>
          <p className="text-slate-500 text-sm mt-1">We use it to find loads picking up near you.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Navigation size={16} className="text-gold" />
            <span><span className="font-bold text-navy" data-testid="matches-count">{data.count}</span> load(s) within {data.radius} mi of {data.home_zip}</span>
          </div>
          {data.count === 0 && (
            <div className="card-base p-8 text-center text-slate-500" data-testid="no-matches">No published loads in range yet. Widen your radius or check back soon.</div>
          )}
          <div className="grid md:grid-cols-2 gap-4" data-testid="matches-list">
            {data.matches.map((m) => (
              <div key={m.load_id} className="card-base p-5 hover:border-gold transition-colors cursor-pointer"
                onClick={() => navigate(`/app/loads/${m.load_id}`)} data-testid={`match-${m.load_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-bold text-navy leading-tight">
                      {m.pickup_city}, {m.pickup_state} → {m.dropoff_city}, {m.dropoff_state}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{m.distance} mi haul · {m.weight?.toLocaleString()} lbs</div>
                  </div>
                  <span className="inline-flex items-center gap-1 bg-gold/15 text-navy text-[11px] font-bold px-2 py-1 rounded-full whitespace-nowrap">
                    <Navigation size={11} /> {m.match_distance_mi} mi away
                  </span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="badge published capitalize flex items-center gap-1"><Truck size={11} /> {m.equipment_type}</span>
                    {m.equipment_match && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700"><CheckCircle2 size={12} /> Your rig</span>}
                  </div>
                  <div className="font-display text-xl font-bold text-navy">{fmtMoney(m.rate)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
